import { useCallback, useEffect, useRef, useState } from 'react';
import { Notification, NotificationState, WebSocketMessage } from './types';
import { getNotificationWsUrl } from './utils';

const INITIAL_RECONNECT_DELAY = 1000;   // 1 second
const MAX_RECONNECT_DELAY = 30000;      // 30 seconds
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Low-level WebSocket hook that manages the connection lifecycle,
 * auto-reconnects with exponential backoff, and exposes notification state.
 *
 * Designed to be consumed by NotificationContext (single global instance).
 */
export const useNotificationSocket = () => {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isConnected: false,
    isConnecting: false,
    error: null,
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const isMountedRef = useRef(true);

  // ── helpers ───────────────────────────────────────────────────────

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /** Backend uses status=0 for pending (unread) notifications */
  const computeUnreadCount = useCallback((notifications: Notification[]): number => {
    return notifications.filter((n) => n.status === 0).length;
  }, []);

  // ── connect ───────────────────────────────────────────────────────

  const connect = useCallback(() => {
    // Prevent connecting if already connected or if component unmounted
    if (wsRef.current?.readyState === WebSocket.OPEN || !isMountedRef.current) {
      return;
    }

    const wsUrl = getNotificationWsUrl();

    if (!wsUrl) {
      setState((prev) => ({
        ...prev,
        error: 'Unable to build WebSocket URL (missing login data or env config)',
        isConnecting: false,
      }));
      return;
    }

    // Close any previous connection that may be in a transitional state
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setState((prev) => ({ ...prev, isConnecting: true, error: null }));

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;

        console.log('[Notification WS] Connected');
        reconnectAttemptRef.current = 0;

        setState((prev) => ({
          ...prev,
          isConnected: true,
          isConnecting: false,
          error: null,
        }));

        // Request existing notifications
        ws.send(JSON.stringify({ type: 'get_notifications' }));
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!isMountedRef.current) return;

        try {
          const data: WebSocketMessage = JSON.parse(event.data);
          handleMessage(data);
        } catch (err) {
          console.error('[Notification WS] Failed to parse message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('[Notification WS] Error:', event);
        if (!isMountedRef.current) return;

        setState((prev) => ({
          ...prev,
          error: 'WebSocket connection error',
        }));
      };

      ws.onclose = (event) => {
        if (!isMountedRef.current) return;

        console.log('[Notification WS] Closed', event.code, event.reason);

        setState((prev) => ({
          ...prev,
          isConnected: false,
          isConnecting: false,
        }));

        // Attempt reconnect (unless intentionally closed or max attempts exceeded)
        if (event.code !== 1000 && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          scheduleReconnect();
        }
      };
    } catch (err) {
      console.error('[Notification WS] Failed to create WebSocket:', err);
      setState((prev) => ({
        ...prev,
        isConnecting: false,
        error: 'Failed to create WebSocket connection',
      }));
      scheduleReconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── message handler ───────────────────────────────────────────────

  const handleMessage = useCallback(
    (data: WebSocketMessage) => {
      switch (data.type) {
        // Bulk notification list — response to `get_notifications`
        case 'recent_notifications':
        case 'notification_list':
        case 'notifications': {
          const list: Notification[] = data.notifications || [];
          setState((prev) => ({
            ...prev,
            notifications: list,
            unreadCount: data.unread_count ?? computeUnreadCount(list),
          }));
          break;
        }

        // A new real-time notification pushed from the server
        case 'new_notification':
        case 'notification': {
          const newNotif = data.notification;
          if (newNotif) {
            setState((prev) => {
              const updated = [newNotif, ...prev.notifications];
              return {
                ...prev,
                notifications: updated,
                unreadCount: data.unread_count ?? computeUnreadCount(updated),
              };
            });
          }
          break;
        }

        // Unread count update from server
        case 'unread_count': {
          if (typeof data.unread_count === 'number') {
            setState((prev) => ({ ...prev, unreadCount: data.unread_count! }));
          }
          break;
        }

        default:
          console.log('[Notification WS] Unhandled message type:', data.type);
      }
    },
    [computeUnreadCount],
  );

  // ── reconnect logic ───────────────────────────────────────────────

  const scheduleReconnect = useCallback(() => {
    clearReconnectTimer();

    const attempt = reconnectAttemptRef.current;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[Notification WS] Max reconnect attempts reached');
      setState((prev) => ({
        ...prev,
        error: 'Unable to connect after multiple attempts',
      }));
      return;
    }

    const delay = Math.min(INITIAL_RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
    console.log(`[Notification WS] Reconnecting in ${delay}ms (attempt ${attempt + 1})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptRef.current += 1;
      connect();
    }, delay);
  }, [clearReconnectTimer, connect]);

  // ── actions exposed to consumers ──────────────────────────────────

  const markAsRead = useCallback((id: number | string) => {
    setState((prev) => {
      const updated = prev.notifications.map((n) =>
        (n.id === id ? { ...n, status: 1, status_display: 'Read' } : n),
      );
      return {
        ...prev,
        notifications: updated,
        unreadCount: computeUnreadCount(updated),
      };
    });

    // Notify the server
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', notification_id: id }));
    }
  }, [computeUnreadCount]);

  const markAllRead = useCallback(() => {
    setState((prev) => ({
      ...prev,
      notifications: prev.notifications.map((n) => ({ ...n, status: 1, status_display: 'Read' })),
      unreadCount: 0,
    }));

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_all_as_read' }));
    }
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttemptRef.current = 0;
    connect();
  }, [connect]);

  // ── lifecycle ─────────────────────────────────────────────────────

  useEffect(() => {
    isMountedRef.current = true;
    connect();

    return () => {
      isMountedRef.current = false;
      clearReconnectTimer();

      if (wsRef.current) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    markAsRead,
    markAllRead,
    reconnect,
  };
};
